import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';

interface SearchResult {
  title: string;
  url: string;
  source: string;
  distance: number;
  matchType: string;
}

interface SearchResponse {
  query: string;
  maxDistance: number;
  searchType: string;
  results: SearchResult[];
}

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [maxDistance, setMaxDistance] = useState('0.5');
  const [searchType, setSearchType] = useState('content');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        maxDistance,
        searchType,
      });
      const res = await fetch(`/api/embedding-search?${params}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed: ${res.status}`);
      }
      const data: SearchResponse = await res.json();
      setResults(data.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-search">
      <Header />
      <Link className="back-link" to="/">
        &larr; Back to headlines
      </Link>

      <h2>Embedding Search</h2>
      <form onSubmit={handleSubmit} className="search-form">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search text..."
          className="search-input"
        />
        <label className="distance-label">
          Max distance
          <input
            type="number"
            step="0.01"
            min="0"
            max="2"
            value={maxDistance}
            onChange={(e) => setMaxDistance(e.target.value)}
            className="distance-input"
          />
        </label>
        <label className="distance-label">
          Search in
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="search-type-select"
          >
            <option value="both">Title + Content</option>
            <option value="title">Title only</option>
            <option value="content">Content only</option>
          </select>
        </label>
        <button type="submit" disabled={loading || !query.trim()}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <p className="error">{error}</p>}

      {results !== null && (
        <div className="search-results">
          <p className="result-count">{results.length} results</p>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Dist</th>
                <th>Headline</th>
                <th>Source</th>
                <th>Match</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={`${r.url}-${i}`}>
                  <td className="col-rank">{i + 1}</td>
                  <td className="col-dist">{r.distance.toFixed(4)}</td>
                  <td>
                    <a href={r.url} target="_blank" rel="noreferrer">
                      {r.title}
                    </a>
                  </td>
                  <td className="col-source">{r.source}</td>
                  <td className="col-match">{r.matchType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
